'use client';

import clsx from 'clsx';
import { FC, useState } from 'react';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { Body, Title } from 'atoms/Typography/Typography';
import { ButtonWithIcon } from 'molecules/ButtonWithIcon';
import { Price } from 'molecules/Price';
import { ProductImage } from 'molecules/ProductImage';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ProductSize } from 'molecules/ProductSize';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { VariantList } from 'molecules/VariantList';
import { Vat } from 'molecules/Vat';
import {
  PriceInfo,
  ProductQuantitySelect,
} from 'organisms/ProductPriceSection';
import { ProductDiscount } from 'organisms/ProductPriceSection/PriceInfo';
import { SizeSelectBare } from 'organisms/ProductPriceSection/SizeSelect';
import { ProductMaterialInfo } from 'pages/ProductDetail/ProductMaterialInfo';
import { ProductSizesProvider } from 'pages/ProductDetail/context';
import {
  useProductSizeSelect,
  useVariantsWithProduct,
} from 'pages/ProductDetail/hooks';
import { RichTextMarkdown } from 'pages/StoryBlok/RichText';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { IAdaptedBasicProduct } from 'commerce/scayle/utils/adapter/types';
import { IProductInfo, ISelectedProduct } from '../types';
import styles from './styles.module.css';

export const ProductInfo: FC<IProductInfo> = ({ product }) => {
  const { referenceKey, productDescription, isSaleable, color } = product;
  const t = useTranslations();
  const { isDesktop } = useMediaQuery();
  const { gtmData } = useSiteConfig();
  const { addToCart } = useCart();

  const [state, setState] = useState({
    quantity: 1,
    productRefKey: product.referenceKey,
  });
  const variantsWithProduct = useVariantsWithProduct(product);

  const siblings = product.siblings || [];
  const productWithSiblingsByRefKey: Record<string, IAdaptedBasicProduct> = {
    [referenceKey]: product,
  };
  siblings.forEach(
    (sibling) => (productWithSiblingsByRefKey[sibling.referenceKey] = sibling),
  );

  const selectedProduct = productWithSiblingsByRefKey[
    state.productRefKey
  ] as ISelectedProduct;

  const { brand, brandLink, size: productSize } = selectedProduct;

  const {
    hasSizes,
    sizeOptions,
    selectedVariant,
    quantityLimit,
    setSelectedVariant,
  } = useProductSizeSelect(selectedProduct, selectedProduct.referenceKey, true);

  const activeVariant =
    selectedProduct.variants?.find(
      (variant) => variant.referenceKey === selectedVariant,
    ) || selectedProduct.variants?.[0];

  const isOutOfStock =
    !activeVariant?.stock || activeVariant.stock.quantity === 0;
  const isDisabled = isOutOfStock || (hasSizes && !selectedVariant);

  const transformedSizeOptions = sizeOptions.map((option) => ({
    ...option,
    label:
      option.disabled && option.value
        ? t('product.pdp.soldOut', { label: option.label || '' })
        : option.label,
  }));

  const isDescriptionVisible =
    productDescription && ((isSaleable && isDesktop) || !isSaleable);

  const setActiveVariant = (variantReferenceKey: string) => {
    setState((s) => ({ ...s, productRefKey: variantReferenceKey }));
  };

  const handleClick = async () => {
    if (!activeVariant?.referenceKey) {
      return;
    }

    addToCart(
      activeVariant.referenceKey,
      state.quantity,
      gtmData?.pageType,
      selectedProduct.limitForCart,
    );
  };

  return (
    <ProductSizesProvider product={selectedProduct}>
      <div className={styles.tile}>
        <ProductImage
          hash={selectedProduct.coverImage}
          alt={selectedProduct.name}
          className={styles.img}
        />
        <div className={styles.productInfo}>
          {!isSaleable &&
            brand &&
            (brandLink ? (
              <Link
                href={brandLink}
                className={clsx(styles.brandLink, styles.brand)}
              >
                <Body
                  type="sm"
                  tag="span"
                  weight="medium"
                  className={styles.brand}
                >
                  {brand}
                </Body>
              </Link>
            ) : (
              <Body
                type="sm"
                tag="span"
                weight="medium"
                className={styles.subTitleText}
              >
                {brand}
              </Body>
            ))}

          <Title type="md" tag="h2" className={styles.productName}>
            <ParseBBCode content={product.productName} />
          </Title>

          <div className={styles.metaInfo}>
            <ProductMaterialInfo
              size={productSize}
              composedMaterials={product.composedMaterials}
            />
            {productSize && <ProductSize size={productSize} />}
            {activeVariant?.price && (
              <ProductReferencePrice price={activeVariant.price} />
            )}
          </div>
          {!isSaleable && (
            <ReviewsRating
              url={selectedProduct.href}
              value={product?.ratingScore}
              count={product?.ratingCount}
            />
          )}

          {isDescriptionVisible && (
            <RichTextMarkdown
              text={productDescription}
              components={{
                p: ({ children }) => (
                  <Body type="lg" weight="medium" className={styles.text}>
                    {children}
                  </Body>
                ),
              }}
            />
          )}

          {isSaleable && (
            <>
              {color.value && (
                <VariantList
                  variants={variantsWithProduct}
                  currentProductId={String(selectedProduct.id)}
                  isLink={false}
                  setActiveVariant={setActiveVariant}
                />
              )}

              <PriceInfo type="column" className={styles.prices}>
                <ProductDiscount
                  discountPercentage={selectedProduct.discountPercentage}
                  recommendedPrice={
                    selectedProduct.price.recommendedRetailPrice
                  }
                  currencyCode={selectedProduct.price.currencyCode}
                />
                <Price
                  amount={selectedProduct.price.withTax}
                  currencyCode={selectedProduct.price.currencyCode}
                  isCrossed={false}
                  size="large"
                />
              </PriceInfo>

              <div className={styles.buttonsBlock}>
                {hasSizes && (
                  <SizeSelectBare
                    options={transformedSizeOptions}
                    classes={{
                      root: clsx(styles.productDetailSelect, styles.sizeSelect),
                    }}
                    value={selectedVariant}
                    onChange={(event) => setSelectedVariant(event.target.value)}
                  />
                )}
                {!!selectedVariant && (
                  <ProductQuantitySelect
                    setQuantity={(quantity) =>
                      setState((s) => ({ ...s, quantity }))
                    }
                    quantityLimit={quantityLimit}
                    isOutOfStock={isDisabled}
                    className={clsx(
                      styles.productDetailSelect,
                      styles.quantitySelect,
                    )}
                  />
                )}
                <ButtonWithIcon
                  color="deep-purple"
                  iconSvgType="cartWhite"
                  size="large"
                  className={styles.button}
                  onClick={handleClick}
                  disabled={isDisabled}
                >
                  {t('common.buttons.buy')}
                </ButtonWithIcon>
              </div>
              <Vat rate={selectedProduct.taxRatePercent} />
            </>
          )}
        </div>
      </div>
    </ProductSizesProvider>
  );
};
